import { EventEmitter, Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { HttpClient, HttpEvent, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { HEADERS, Library, OPTIONS } from '../library';
import { Location } from '@angular/common';
import { NewAccount } from 'app/shared/models/new-account';
import { SharedService } from '../services/shared.service';
import { NewUserPass } from 'app/shared/models/newUserPass';
import { CookieService } from 'ngx-cookie-service';
import { DevicesDialogService } from '../services/devices-dialog.service';
import { TabService } from '../services/tab.service';
import { Account } from 'app/shared/models/account';
import { Token } from 'app/shared/models/token';
import { ToastrService } from 'ngx-toastr';
import { VerifyModalComponent } from 'app/auth-pages/verify-modal/verify-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

declare var $: any;

@Injectable()
export class AuthService {

  decodedToken: any;
  decodedTokenSource = new Subject<string>();
  decodedToken$ = this.decodedTokenSource.asObservable();
  jwtHelper: JwtHelperService = new JwtHelperService();

  logoutSource = new Subject<boolean>();
  logout$ = this.logoutSource.asObservable();


  public loggedIn = false;
  private name = '';
  isLoading: boolean = false;
  registered: boolean = false;
  planSuccess: boolean = false;

  top: string = 'top';
  center: string = 'center';
  loadingButtonId: number | null = null;

  constructor(private http: HttpClient, private router: Router, private sharedService: SharedService, private cookieService: CookieService,
    private toastr: ToastrService, private modalService: NgbModal, private tabService: TabService) {
  }

  showNotification(text) {
    this.toastr.success('<span class="now-ui-icons ui-1_bell-53"></span> ' + text + '', '', {
      timeOut: 5000,
      closeButton: true,
      enableHtml: true,
      toastClass: "alert alert-danger alert-with-icon",
      positionClass: 'toast-' + 'top' + '-' + 'center'
    });
  }

  login(username: string, password: string, redirectTo: string) {
    localStorage.removeItem('IsSubServiceProvider');
    this.isLoading = true;
    this.http.post<Token>(Library.API_ENDPOINT + `auth/login`, { username, password }, OPTIONS)
      .subscribe(
        (response: Token) => {
          localStorage.setItem('jwt', response.token);
          if (response.refreshToken) {
            localStorage.setItem('refreshToken', response.refreshToken);
          }
          // this.cookieService.set('jwt', response.token);
          this.onDecodedToken(response.token);
          this.getAccount().subscribe(accountResponse => {
            localStorage.setItem('account', JSON.stringify(accountResponse));
            if (accountResponse.isServiceProviderClient) {
              localStorage.setItem('isLoggedInAsSPClient', JSON.stringify(true));
            } else {
              localStorage.removeItem('isLoggedInAsSPClient');
            }
            if (accountResponse.isSuperUser) {
              this.router.navigate(['dashboard']).then(() => {
                window.location.reload();
              });
            } else {
              this.router.navigate(['home']).then(() => {
                window.location.reload();
              });
            }
          });
        },
        error => {
          this.loggedIn = false;
          const text = 'Credentials are wrong.'
          this.showNotification(text);
          this.decodedToken = null;
          this.isLoading = false;
        }
      );
  }

  getAccount() {
    return this.http.get<Account>(Library.API_ENDPOINT + 'account/info', OPTIONS);
  }

  signup(account: NewAccount, redirectTo: String) {
    delete account.confirmPassword;
    this.isLoading = true
    this.http.post<Token>(Library.API_ENDPOINT + `account/signup/`, account, OPTIONS).subscribe(
      (response: Token) => {
        // Handle successful response
        this.loggedIn = true;
        localStorage.setItem('jwt', response.token);
        // this.cookieService.set('jwt', response.token);
        localStorage.setItem('email', account.email);
        localStorage.setItem('name', account.name);
        this.onDecodedToken(response.token);
        this.router.navigateByUrl('/' + redirectTo).then(() => {
          this.isLoading = false;
        });
      },
      (errorResponse) => {
        this.loggedIn = false;
        this.decodedToken = null;
        const text = 'Something went wrong.'
        this.showNotification(text);
        this.isLoading = false
      }
    );
  }

  verify(deviceName: string, username: string, password: string) {
    this.isLoading = true;

    const params = new HttpParams()
      .set('deviceName', deviceName)
      .set('value', username);

    return this.http.post<any>(
      Library.API_ENDPOINT + 'account/verify',
      { username, password },
      { params }
    );
  }

  verifyServiceProvider(deviceName: string, username: string, password: string) {
    this.isLoading = true;

    const params = new HttpParams()
      .set('deviceName', deviceName)
      .set('value', username);

    return this.http.post<any>(
      Library.API_ENDPOINT + 'account/verify',
      { username, password },
      { params }
    );
  }

  verifyReferenceID(referenceID: String) {

    this.isLoading = true;
    return this.http.post<any>(Library.API_ENDPOINT + `account/verify_referenceID/${referenceID}`, OPTIONS).subscribe(
      response => {
        if (response.message === 'Verified') {
          localStorage.setItem('referenceID', JSON.stringify(referenceID));
          this.getNewUser(referenceID);
        } else if (response.message === 'Reference doesnt exists') {
          this.isLoading = false;
          this.showNotification('Reference ID doesnt exists');
        }
      },
      error => {
        // Handle HTTP error here if necessary
        this.isLoading = false;
        console.error('An error occurred:', error);
        this.showNotification('An unexpected error occured.');
      }
    );
  }

  getNewUser(referenceID: String) {
    return this.http.post<any>(Library.API_ENDPOINT + `account/get_newUser/${referenceID}`, OPTIONS).subscribe(
      response => {
        if (response.message === 'Failed to retrieve user') {
          this.isLoading = false;
          this.showNotification('Failed to retrieve user.');
        } else {
          localStorage.setItem('name', JSON.stringify(response.message));
          this.sharedService.triggerOpenModal();
          this.isLoading = false;
        }
      },
      error => {
        // Handle HTTP error here if necessary
        this.isLoading = false;
        console.error('An error occurred:', error);
        this.showNotification('An unexpected error occured.');
      }
    );
  }

  requestPasswordReset(email: string): Observable<any> {
    const url = Library.API_ENDPOINT + `account/forgot-password`;

    return this.http.post(url, { email }, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    });
  }

  setPassword(pass: NewUserPass) {
    this.isLoading = true;

    return this.http.post<any>(Library.API_ENDPOINT + 'account/setup_password', pass, OPTIONS)
      .subscribe(
        response => {
          if (response.message === 'Password Set') {
            this.isLoading = false;
            this.router.navigate(['success']);
          } else if (response.message === 'Failed to set password') {
            this.isLoading = false;
            this.showNotification('Failed to set password.');
          }
        },
        error => {
          // Handle HTTP error here if necessary
          this.isLoading = false;
          console.error('An error occurred:', error);
          this.showNotification('An unexpected error occurred.');
        }
      );
  }

  resetPassword(data: { userId: number, uuid: string, newPassword: string }): Observable<any> {
    const url = `${Library.API_ENDPOINT}account/reset-password`;

    return this.http.post(url, data, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    });
  }

  updateToken(tokenJson: Token) {
    // this.cookieService.set('jwt', tokenJson.token || '');
    // this.onDecodedToken(tokenJson.token || '');
    localStorage.setItem('jwt', tokenJson.token || '');
    this.onDecodedToken(tokenJson.token || '');
  }

  testIsAuthenticated() {
    const jwt = localStorage.getItem('jwt');
    // const jwt = this.cookieService.get('jwt');
    if (jwt) {
      this.onDecodedToken(jwt);
      this.loggedIn = true;
    }
  }

  isAuthenticated() {
    return this.loggedIn;
  }

  getName() {
    return this.name;
  }

  public getToken(): string {
    return localStorage.getItem('jwt') as string;
    // return this.cookieService.get('jwt') as string;
  }

  onDecodedToken(token: string) {
    try {
      this.decodedToken = token ? this.jwtHelper.decodeToken(token) : this.decodedToken = null;
      this.name = this.decodedToken.name;
      this.decodedTokenSource.next(this.decodedToken);
    } catch (e) {
      this.logout();
    }
  }

  refreshAccessToken(): Observable<Token> {
    const refreshToken = localStorage.getItem('refreshToken');
    return this.http.post<Token>(Library.API_ENDPOINT + 'auth/refresh', { refreshToken }, OPTIONS);
  }

  logout() {
    this.loggedIn = false;
    this.decodedToken = null;
    localStorage.removeItem('jwt');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('account');
    localStorage.removeItem('isPassConfirm');
    localStorage.removeItem('isPassConfirm_access');
    this.tabService.closeAll();
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.includes('TableColumns')) {
        localStorage.removeItem(key);
        i--;
      }
    }
    // this.cookieService.delete('jwt');
    this.logoutSource.next(true);
    window.location.href = '/#/login';
  }

  // New Endpoint for Countries and Regions
  getCountries() {
    return this.http.get<any>(Library.API_ENDPOINT + `common/countries`, OPTIONS);
  }

  getRegion(C_Country_ID: number) {
    return this.http.get<any>(Library.API_ENDPOINT + `common/region/${C_Country_ID}`, OPTIONS);
  }

  getProvince(C_Region_ID: number) {
    return this.http.get<any>(Library.API_ENDPOINT + `common/city/${C_Region_ID}`, OPTIONS);
  }

  registerClient(clientDetails: any) {
    this.isLoading = true;
    this.http.post<any>(Library.API_ENDPOINT + `account/register`, clientDetails, OPTIONS).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.message === 'Register Success!') {
          localStorage.setItem('email', clientDetails.email);
          const modalRef = this.modalService.open(VerifyModalComponent, {
            backdrop: 'static',
            keyboard: false,
            centered: true
          });
          modalRef.componentInstance.clientID = res.ID;
          // this.router.navigate(['plan'], { replaceUrl: true });
        } else {
          this.showNotification(res.message);
        }
      },
      error: err => {
        this.isLoading = false;
        console.log(err);
        this.showNotification(err);
      }
    });
  }

  registerSP(clientDetails: any) {
    this.isLoading = true;
    this.http.post<any>(Library.API_ENDPOINT + `account/register_sp`, clientDetails, OPTIONS).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.message === 'Register Success!') {
          localStorage.setItem('email', clientDetails.email);
          const modalRef = this.modalService.open(VerifyModalComponent, {
            backdrop: 'static',
            keyboard: false,
            centered: true
          });
          modalRef.componentInstance.clientID = res.ID;
          // this.router.navigate(['plan'], { replaceUrl: true });
        } else {
          this.showNotification(res.message);
        }
      },
      error: err => {
        this.isLoading = false;
        console.log(err);
        this.showNotification(err);
      }
    });
  }

  registerSPClient(clientDetails: any) {
    return this.http.post<any>(Library.API_ENDPOINT + `account/register_sp_client`, clientDetails, OPTIONS)
  }

  isRegistered() {
    return this.registered;
  }

  setIsRegistered(registered: boolean) {
    this.registered = registered;
  }

  getPlans() {
    return this.http.get<any>(Library.API_ENDPOINT + `account/get_plans`, OPTIONS);
  }

  insertPlan(email: string, id: number, indexNumber: number) {
    this.isLoading = true;
    this.loadingButtonId = indexNumber;
    this.http.post<any>(Library.API_ENDPOINT + `account/insert_plan/${email}/${id}`, OPTIONS).subscribe({
      next: res => {
        console.log(res);
        this.isLoading = false;
        if (res.message === 'Plan inserted') {
          this.planSuccess = true;
          localStorage.setItem('email', email);
          this.isLoading = false;
          this.loadingButtonId = null;
          this.router.navigate(['plan/success'], { replaceUrl: true });
        } else {
          this.loadingButtonId = null;
          this.showNotification(res.message);
        }
      },
      error: err => {
        this.isLoading = false;
        this.loadingButtonId = null;
        console.log(err);
        this.showNotification(err);
      }
    });
  }

  isPlanSuccess() {
    return this.planSuccess;
  }

  setisPlanSuccess(planSuccess: boolean) {
    this.planSuccess = planSuccess;
  }

  getVerification(ID: any) {
    return this.http.get<any>(Library.API_ENDPOINT + `account/get_verification/${ID}`, OPTIONS);
  }

  verifyEmail(id: number) {
    return this.http.post<any>(Library.API_ENDPOINT + `account/verify_email/${id}`, OPTIONS)
  }
}
